import React, { useState } from 'react';
import {
  Search,
  Moon,
  Sun,
  MapPin,
  Bell,
  Maximize2,
  Zap,
  Music,
  ExternalLink,
  X,
} from 'lucide-react';

export default function HeaderNav({
  theme,
  setTheme,
  searchTerm,
  setSearchTerm,
  cityFilter,
  setCityFilter,
  onLogout,
  rooms = [],
  captains = [],
  users = [],
  publishedTracks = [],
  setActiveTab,
  onSelectDrawer,
  onOpenInstallPrompt,
}) {
  const [showNotifications, setShowNotifications] = useState(false);
  const [showSearchDropdown, setShowSearchDropdown] = useState(false);
  const [showCommandModal, setShowCommandModal] = useState(false);

  // Real Dynamic Notifications derived from platform data
  const dynamicNotifs = [];
  const pendingCaptains = captains.filter(c => c.status === 'pending' || (!c.isApproved && c.status !== 'blocked'));
  if (pendingCaptains.length > 0) {
    dynamicNotifs.push({
      id: 'notif-cap',
      title: 'Captain Verification Queue',
      msg: `${pendingCaptains.length} captain account(s) waiting for credentials moderation.`,
      time: 'Live DB',
      type: 'captain',
      read: false,
      tab: 'approvals'
    });
  }

  const pendingRooms = rooms.filter(r => r.status === 'pending');
  if (pendingRooms.length > 0) {
    dynamicNotifs.push({
      id: 'notif-room',
      title: 'Listing Moderation Queue',
      msg: `${pendingRooms.length} room/party listing(s) pending approval.`,
      time: 'Live DB',
      type: 'loka',
      read: false,
      tab: 'approvals'
    });
  }

  if (publishedTracks.length > 0) {
    dynamicNotifs.push({
      id: 'notif-track',
      title: 'Live Audio Anthems',
      msg: `${publishedTracks.length} track(s) streaming live in floating bubble player.`,
      time: 'Online',
      type: 'music',
      read: true,
      tab: 'music'
    });
  }

  const [notifications, setNotifications] = useState([]);
  const [hasInitializedNotifs, setHasInitializedNotifs] = useState(false);

  React.useEffect(() => {
    if (dynamicNotifs.length > 0 && !hasInitializedNotifs) {
      setNotifications(dynamicNotifs);
      setHasInitializedNotifs(true);
    } else if (hasInitializedNotifs) {
      // Update with latest real items
      setNotifications(prev => {
        const readIds = new Set(prev.filter(p => p.read).map(p => p.id));
        return dynamicNotifs.map(n => ({ ...n, read: readIds.has(n.id) }));
      });
    }
  }, [captains.length, rooms.length, publishedTracks.length]);

  const toggleTheme = () => {
    const nextTheme = theme === 'dark' ? 'light' : 'dark';
    setTheme(nextTheme);
    localStorage.setItem('theme', nextTheme);
    document.documentElement.setAttribute('data-theme', nextTheme);
  };

  const toggleFullScreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen().catch(() => {});
    } else if (document.exitFullscreen) {
      document.exitFullscreen().catch(() => {});
    }
  };

  const unreadCount = notifications.filter((n) => !n.read).length;

  const markAllNotificationsRead = () => {
    setNotifications(notifications.map((n) => ({ ...n, read: true })));
  };

  const clearAllNotifications = () => {
    setNotifications([]);
  };

  // Live Instant Search Filter Results
  const term = searchTerm.trim().toLowerCase();
  const searchResultsRooms = term
    ? rooms.filter(
        (r) =>
          r.title?.toLowerCase().includes(term) ||
          r.city?.toLowerCase().includes(term) ||
          r.categoryType?.toLowerCase().includes(term) ||
          r.moduleType?.toLowerCase().includes(term)
      ).slice(0, 4)
    : [];

  const searchResultsCaptains = term
    ? captains.filter(
        (c) =>
          c.fullName?.toLowerCase().includes(term) ||
          c.name?.toLowerCase().includes(term) ||
          c.phone?.includes(term) ||
          c.email?.toLowerCase().includes(term)
      ).slice(0, 3)
    : [];

  const searchResultsTracks = term
    ? publishedTracks.filter(
        (t) =>
          t.title?.toLowerCase().includes(term) ||
          t.artist?.toLowerCase().includes(term) ||
          t.genre?.toLowerCase().includes(term)
      ).slice(0, 3)
    : [];

  const totalResultsCount =
    searchResultsRooms.length + searchResultsCaptains.length + searchResultsTracks.length;

  return (
    <header className="top-navbar">
      {/* Brand Header */}
      <div className="navbar-brand">
        <div
          style={{
            width: '12px',
            height: '12px',
            borderRadius: '8px',
            background: 'rgba(0, 255, 135, 0.15)',
            border: '1px solid rgba(0, 255, 135, 0.4)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 0 12px rgba(0, 255, 135, 0.2)',
          }}
        />
        <span style={{ fontSize: '1.25rem', fontWeight: 900, color: 'var(--text-primary)', fontFamily: 'var(--font-sans)', letterSpacing: '-0.02em' }}>
          I BHARAT <span style={{ color: 'var(--neon-green)', fontSize: '0.75rem', fontFamily: 'var(--font-mono)' }}>CONSOLE</span>
        </span>
      </div>

      {/* Center & Right Controls */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
        {/* PWA / Home Screen Shortcut Quick Button */}
        <button
          onClick={onOpenInstallPrompt}
          style={{
            background: 'linear-gradient(135deg, rgba(0, 255, 135, 0.15) 0%, rgba(0, 200, 255, 0.15) 100%)',
            border: '1px solid var(--neon-green)',
            color: 'var(--neon-green)',
            borderRadius: 'var(--radius-sm)',
            padding: '6px 12px',
            fontSize: '0.78rem',
            fontWeight: 800,
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            cursor: 'pointer',
            fontFamily: 'var(--font-mono)',
            boxShadow: '0 0 12px rgba(0, 255, 135, 0.2)',
          }}
          title="Install RAPMAP Admin shortcut on your Home Screen or Desktop"
        >
          <span>📲</span> INSTALL SHORTCUT
        </button>

        {/* Quick Command Trigger Button */}
        <button
          onClick={() => setShowCommandModal(true)}
          style={{
            background: 'rgba(0, 240, 255, 0.12)',
            border: '1px solid rgba(0, 240, 255, 0.35)',
            color: 'var(--neon-cyan)',
            borderRadius: 'var(--radius-sm)',
            padding: '6px 12px',
            fontSize: '0.78rem',
            fontWeight: 800,
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            cursor: 'pointer',
            fontFamily: 'var(--font-mono)',
          }}
        >
          <Zap size={14} /> COMMANDS
        </button>

        {/* Region Filter */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            padding: '6px 12px',
            background: 'rgba(0, 0, 0, 0.4)',
            border: '1px solid var(--border-color)',
            borderRadius: 'var(--radius-sm)',
            fontSize: '0.78rem',
            color: 'var(--text-secondary)',
          }}
        >
          <MapPin size={13} style={{ color: 'var(--neon-green)' }} />
          <span style={{ fontFamily: 'var(--font-mono)', fontSize: '0.72rem' }}>REGION:</span>
          <select
            value={cityFilter}
            onChange={(e) => setCityFilter(e.target.value)}
            style={{
              background: 'transparent',
              border: 'none',
              color: 'var(--text-primary)',
              fontWeight: 700,
              fontSize: '0.78rem',
              outline: 'none',
              cursor: 'pointer',
            }}
          >
            <option value="" style={{ background: 'var(--bg-surface)' }}>All Nodes</option>
            <option value="Indore" style={{ background: 'var(--bg-surface)' }}>Indore Node</option>
            <option value="Bhopal" style={{ background: 'var(--bg-surface)' }}>Bhopal Node</option>
          </select>
        </div>

        {/* Global Working Searchbar with Live Overlay Dropdown */}
        <div className="search-input-wrapper" style={{ position: 'relative' }}>
          <input
            type="text"
            placeholder="Search for results..."
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setShowSearchDropdown(true);
            }}
            onFocus={() => setShowSearchDropdown(true)}
          />
          <Search size={14} />

          {/* Live Search Results Dropdown Overlay */}
          {showSearchDropdown && term && (
            <div
              style={{
                position: 'absolute',
                top: '45px',
                right: 0,
                width: '380px',
                background: 'var(--bg-surface-elevated)',
                backdropFilter: 'blur(20px)',
                WebkitBackdropFilter: 'blur(20px)',
                border: '1px solid var(--neon-cyan)',
                borderRadius: 'var(--radius-md)',
                boxShadow: 'var(--shadow-lg), 0 0 25px rgba(0, 240, 255, 0.2)',
                padding: '16px',
                zIndex: 200,
                maxHeight: '400px',
                overflowY: 'auto',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px', borderBottom: '1px solid var(--border-color)', paddingBottom: '8px' }}>
                <span style={{ fontSize: '0.72rem', fontWeight: 800, color: 'var(--neon-cyan)', fontFamily: 'var(--font-mono)' }}>
                  SEARCH RESULTS ({totalResultsCount})
                </span>
                <button
                  onClick={() => setShowSearchDropdown(false)}
                  style={{ background: 'transparent', border: 'none', color: 'var(--text-muted)', cursor: 'pointer' }}
                >
                  <X size={14} />
                </button>
              </div>

              {totalResultsCount === 0 ? (
                <div style={{ padding: '16px', textAlign: 'center', fontSize: '0.82rem', color: 'var(--text-muted)' }}>
                  No nodes found matching "{searchTerm}"
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                  {/* Rooms Section */}
                  {searchResultsRooms.length > 0 && (
                    <div>
                      <div style={{ fontSize: '0.68rem', fontWeight: 800, color: 'var(--text-muted)', marginBottom: '6px', fontFamily: 'var(--font-mono)' }}>
                        LISTINGS & HOTZONES
                      </div>
                      {searchResultsRooms.map((r) => (
                        <div
                          key={r._id}
                          onClick={() => {
                            setShowSearchDropdown(false);
                            if (onSelectDrawer) onSelectDrawer(r, 'room');
                          }}
                          style={{
                            padding: '8px 10px',
                            borderRadius: '6px',
                            background: 'rgba(0, 0, 0, 0.3)',
                            marginBottom: '6px',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                          }}
                        >
                          <div>
                            <div style={{ fontSize: '0.85rem', fontWeight: 800, color: 'var(--text-primary)' }}>{r.title}</div>
                            <div style={{ fontSize: '0.72rem', color: 'var(--text-secondary)' }}>{r.city || 'Indore'} • ₹{r.price || 1000}/hr</div>
                          </div>
                          <ExternalLink size={14} style={{ color: 'var(--neon-cyan)' }} />
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Captains Section */}
                  {searchResultsCaptains.length > 0 && (
                    <div>
                      <div style={{ fontSize: '0.68rem', fontWeight: 800, color: 'var(--text-muted)', marginBottom: '6px', fontFamily: 'var(--font-mono)' }}>
                        CAPTAINS DIRECTORY
                      </div>
                      {searchResultsCaptains.map((c) => (
                        <div
                          key={c._id}
                          onClick={() => {
                            setShowSearchDropdown(false);
                            if (onSelectDrawer) onSelectDrawer(c, 'captain');
                          }}
                          style={{
                            padding: '8px 10px',
                            borderRadius: '6px',
                            background: 'rgba(0, 0, 0, 0.3)',
                            marginBottom: '6px',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                          }}
                        >
                          <div>
                            <div style={{ fontSize: '0.85rem', fontWeight: 800, color: 'var(--text-primary)' }}>{c.fullName || c.name}</div>
                            <div style={{ fontSize: '0.72rem', color: 'var(--text-secondary)' }}>{c.phone || c.email}</div>
                          </div>
                          <ExternalLink size={14} style={{ color: 'var(--neon-purple)' }} />
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Audio Tracks Section */}
                  {searchResultsTracks.length > 0 && (
                    <div>
                      <div style={{ fontSize: '0.68rem', fontWeight: 800, color: 'var(--text-muted)', marginBottom: '6px', fontFamily: 'var(--font-mono)' }}>
                        AUDIO TRACKS
                      </div>
                      {searchResultsTracks.map((t) => (
                        <div
                          key={t._id}
                          onClick={() => {
                            setShowSearchDropdown(false);
                            if (setActiveTab) setActiveTab('music');
                          }}
                          style={{
                            padding: '8px 10px',
                            borderRadius: '6px',
                            background: 'rgba(0, 0, 0, 0.3)',
                            marginBottom: '6px',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                          }}
                        >
                          <div>
                            <div style={{ fontSize: '0.85rem', fontWeight: 800, color: 'var(--text-primary)' }}>{t.title}</div>
                            <div style={{ fontSize: '0.72rem', color: 'var(--text-secondary)' }}>{t.artist} • {t.genre}</div>
                          </div>
                          <Music size={14} style={{ color: 'var(--neon-green)' }} />
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Header Action Icons */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          {/* Fullscreen Toggle */}
          <button
            onClick={toggleFullScreen}
            style={{
              background: 'rgba(0, 0, 0, 0.3)',
              border: '1px solid var(--border-color)',
              color: 'var(--text-primary)',
              borderRadius: '6px',
              width: '34px',
              height: '34px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
            }}
            title="Toggle Fullscreen"
          >
            <Maximize2 size={15} style={{ color: 'var(--text-muted)' }} />
          </button>

          {/* Theme Switcher */}
          <button
            onClick={toggleTheme}
            style={{
              background: 'rgba(0, 0, 0, 0.3)',
              border: '1px solid var(--border-color)',
              color: 'var(--text-primary)',
              borderRadius: '6px',
              width: '34px',
              height: '34px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
            }}
            title="Toggle Theme"
          >
            {theme === 'dark' ? <Sun size={15} style={{ color: 'var(--neon-amber)' }} /> : <Moon size={15} style={{ color: 'var(--neon-cyan)' }} />}
          </button>

          {/* Interactive Notifications Bell Dropdown */}
          <div style={{ position: 'relative' }}>
            <button
              onClick={() => setShowNotifications(!showNotifications)}
              style={{
                background: 'rgba(0, 0, 0, 0.3)',
                border: showNotifications ? '1px solid var(--neon-cyan)' : '1px solid var(--border-color)',
                color: 'var(--text-primary)',
                borderRadius: '6px',
                width: '34px',
                height: '34px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                position: 'relative',
              }}
            >
              <Bell size={15} style={{ color: unreadCount > 0 ? 'var(--neon-cyan)' : 'var(--text-muted)' }} />
              {unreadCount > 0 && (
                <span
                  style={{
                    position: 'absolute',
                    top: '-3px',
                    right: '-3px',
                    background: 'var(--neon-rose)',
                    color: '#FFF',
                    fontSize: '0.65rem',
                    fontWeight: 900,
                    borderRadius: '50%',
                    width: '16px',
                    height: '16px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: '0 0 10px var(--neon-rose)',
                  }}
                >
                  {unreadCount}
                </span>
              )}
            </button>

            {/* Notifications Dropdown Window */}
            {showNotifications && (
              <div
                style={{
                  position: 'absolute',
                  top: '45px',
                  right: 0,
                  width: '360px',
                  background: 'var(--bg-surface-elevated)',
                  backdropFilter: 'blur(20px)',
                  WebkitBackdropFilter: 'blur(20px)',
                  border: '1px solid var(--neon-cyan)',
                  borderRadius: 'var(--radius-md)',
                  boxShadow: 'var(--shadow-lg), 0 0 30px rgba(0, 240, 255, 0.25)',
                  padding: '18px',
                  zIndex: 200,
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '14px', borderBottom: '1px solid var(--border-color)', paddingBottom: '10px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <Bell size={16} style={{ color: 'var(--neon-cyan)' }} />
                    <span style={{ fontSize: '0.88rem', fontWeight: 800, color: 'var(--text-primary)' }}>System Alerts</span>
                  </div>

                  <div style={{ display: 'flex', gap: '10px', fontSize: '0.72rem' }}>
                    <button onClick={markAllNotificationsRead} style={{ background: 'transparent', border: 'none', color: 'var(--neon-green)', cursor: 'pointer', fontWeight: 700 }}>
                      Mark Read
                    </button>
                    <button onClick={clearAllNotifications} style={{ background: 'transparent', border: 'none', color: 'var(--neon-rose)', cursor: 'pointer', fontWeight: 700 }}>
                      Clear
                    </button>
                  </div>
                </div>

                {notifications.length === 0 ? (
                  <div style={{ padding: '20px', textAlign: 'center', fontSize: '0.82rem', color: 'var(--text-muted)' }}>
                    No unread notifications
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', maxHeight: '300px', overflowY: 'auto' }}>
                    {notifications.map((n) => (
                      <div
                        key={n.id}
                        style={{
                          padding: '10px 12px',
                          borderRadius: '8px',
                          background: n.read ? 'rgba(0, 0, 0, 0.2)' : 'rgba(0, 240, 255, 0.08)',
                          border: `1px solid ${n.read ? 'transparent' : 'rgba(0, 240, 255, 0.3)'}`,
                          display: 'flex',
                          flexDirection: 'column',
                          gap: '4px',
                        }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                          <span style={{ fontSize: '0.85rem', fontWeight: 800, color: 'var(--text-primary)' }}>{n.title}</span>
                          <span style={{ fontSize: '0.68rem', color: 'var(--text-muted)', fontFamily: 'var(--font-mono)' }}>{n.time}</span>
                        </div>
                        <p style={{ fontSize: '0.78rem', color: 'var(--text-secondary)', margin: 0 }}>{n.msg}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Profile Avatar */}
          <div
            style={{
              width: '32px',
              height: '32px',
              borderRadius: '8px',
              background: 'linear-gradient(135deg, #00FF87, #059669)',
              color: '#040A09',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontWeight: 900,
              fontSize: '0.8rem',
              fontFamily: 'var(--font-mono)',
              border: '1px solid rgba(0, 255, 135, 0.4)',
            }}
          >
            AD
          </div>
        </div>
      </div>

      {/* Quick Command Shortcuts Modal */}
      {showCommandModal && (
        <div className="modal-overlay" onClick={() => setShowCommandModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '480px' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px', borderBottom: '1px solid var(--border-color)', paddingBottom: '12px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Zap size={18} style={{ color: 'var(--neon-cyan)' }} />
                <h3 style={{ fontSize: '1.1rem', margin: 0, color: 'var(--text-primary)' }}>Quick System Commands</h3>
              </div>
              <button onClick={() => setShowCommandModal(false)} style={{ background: 'transparent', border: 'none', color: 'var(--text-muted)', cursor: 'pointer' }}>
                <X size={18} />
              </button>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              {[
                { label: '🎵 Publish Audio Track', tab: 'music' },
                { label: '📍 Add Rap Hotzone Location', tab: 'loka' },
                { label: '🏠 Add Building Property', tab: 'properties' },
                { label: '🏋️ Add Fitness / Turf Space', tab: 'fitness' },
                { label: '🛡️ Review Captain Verifications', tab: 'captains' },
                { label: '💻 View Live VPS Kernel Logs', tab: 'dashboard' },
              ].map((cmd, idx) => (
                <button
                  key={idx}
                  className="cyber-list-item"
                  style={{ cursor: 'pointer', textAlign: 'left', width: '100%' }}
                  onClick={() => {
                    if (setActiveTab) setActiveTab(cmd.tab);
                    setShowCommandModal(false);
                  }}
                >
                  <span style={{ fontSize: '0.88rem', fontWeight: 700, color: 'var(--text-primary)' }}>{cmd.label}</span>
                  <ExternalLink size={14} style={{ color: 'var(--neon-cyan)' }} />
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
